import React, { useEffect } from 'react';
import { updateOwnPassword } from '../../slices/auth/settings/thunk';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from "formik";
import * as Yup from "yup";
import classnames from "classnames";
import { Form, Row, Col, Label, Input, Button, FormFeedback, Alert, Spinner } from "reactstrap";
import { Link } from "react-router-dom";
import { createSelector } from 'reselect';
import { resetPwdChangeErr, resetPwdChangeSuccess } from '../../slices/auth/settings/reducer';
import ErrorAlert from '../../Components/Common/ErrorAlert';
import { setBreadcrumb } from '../../slices/auth/menu/reducer';
import SuccessAlert from '../../Components/Common/SuccessAlert';

/**
 * Functional component for changing user password.
 * @component
 */
function ChangePassword() {
  const dispatch = useDispatch();
  const selectPwdError = createSelector(state => state.Settings, state => ({
    changePwdMsg: state.changePasswordMsg,
    changePwdErr: state.changePasswordError,
    changePwdSuccess: state.changePasswordSuccess
  }));
  const { changePwdMsg, changePwdErr, changePwdSuccess } = useSelector(selectPwdError);
  // Formik
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      oldPassword: "",
      newPassword: "",
      newPassword2: "",
    },
    validationSchema: Yup.object().shape({
      oldPassword: Yup.string().required("Please enter old password"),
      newPassword: Yup.string().required("Please enter new password"),
      newPassword2: Yup.string().oneOf([Yup.ref('newPassword'), null], 'Passwords must match').required('Please confirm your password'),
    }),
    onSubmit: async (values, { resetForm }) => {
      dispatch(updateOwnPassword(values));
      resetForm();
    },
  });

  useEffect(() => {
    dispatch(setBreadcrumb({pageTitle: 'Settings', title: [{name: 'Change Password', src: window.location.href}]}));
  }, []);

  return (
    <Form onSubmit={(e) => {
      e.preventDefault();
      validation.handleSubmit();
      return false;
    }}>
      <Row>
        <Col>
          <ErrorAlert errorState={changePwdErr} errorMessage={changePwdMsg} resetError={resetPwdChangeErr} />
          <SuccessAlert successState={changePwdSuccess} successMessage={changePwdMsg} resetSuccess={resetPwdChangeSuccess} />
        </Col>
      </Row>
      <Row>
        <Col lg={4}>
          <div>
            <div className='w-100 d-flex justify-content-between'>
              <Label htmlFor="oldPassword" className="form-label">Old Password*</Label>
              {validation.touched.oldPassword && !validation.errors.oldPassword ? (
                <FormFeedback valid></FormFeedback>
              ) : null}
              {validation.touched.oldPassword && validation.errors.oldPassword ? (
                <FormFeedback type="invalid">{validation.errors.oldPassword}</FormFeedback>
              ) : null}
            </div>
            <Input
              type="password"
              id="oldPassword"
              name="oldPassword"
              placeholder="Enter current password"
              className={"form-control " + classnames({ "is-valid": validation.touched.oldPassword && !validation.errors.oldPassword })}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={validation.touched.oldPassword && validation.errors.oldPassword ? true : false}
              value={validation.values.oldPassword || ""}
            />
          </div>
        </Col>

        <Col lg={4}>
          <div>
            <div className='w-100 d-flex justify-content-between'>
              <Label htmlFor="newPassword" className="form-label">New
                Password*</Label>
              {validation.touched.newPassword && !validation.errors.newPassword ? (
                <FormFeedback valid>Looks good</FormFeedback>
              ) : null}
              {validation.touched.newPassword && validation.errors.newPassword ? (
                <FormFeedback type="invalid">{validation.errors.newPassword}</FormFeedback>
              ) : null}
            </div>
            <Input type="password"
              name="newPassword"
              id="newPassword"
              placeholder="Enter new password"
              className={"form-control " + classnames({ "is-valid": validation.touched.newPassword && !validation.errors.newPassword })}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={validation.touched.newPassword && validation.errors.newPassword ? true : false}
              value={validation.values.newPassword || ""}
            />
          </div>
        </Col>

        <Col lg={4}>
          <div>
            <div className='w-100 d-flex justify-content-between'>
              <Label htmlFor="newPassword2" className="form-label">Confirm
                Password*</Label>
              {validation.touched.newPassword2 && !validation.errors.newPassword2 ? (
                <FormFeedback valid>Looks good</FormFeedback>
              ) : null}
              {validation.touched.newPassword2 && validation.errors.newPassword2 ? (
                <FormFeedback type="invalid">{validation.errors.newPassword2}</FormFeedback>
              ) : null}
            </div>
            <Input type="password"
              name="newPassword2"
              id="newPassword2"
              placeholder="Confirm password"
              className={"form-control " + classnames({ "is-valid": validation.touched.newPassword2 && !validation.errors.newPassword2 })}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={validation.touched.newPassword2 && validation.errors.newPassword2 ? true : false}
              value={validation.values.newPassword2 || ""}
            />
          </div>
        </Col>

        <Col lg={12}>
          <div className="mt-3 mb-3">
            <Link to="#"
              className="link-danger text-decoration-underline">Forgot
              Password ?</Link>
          </div>
        </Col>

        <Col lg={12}>
          <div className="text-end">
            <Button type="submit" className="btn btn-success">
              Change Password
            </Button>
          </div>
        </Col>
      </Row>
    </Form>
  )
}

export default ChangePassword
